// @flow
import * as React from 'react'
import styled from 'styled-components'
import Modal from '../../shared/Modal'
import { Elements } from '@stripe/react-stripe-js'
import PromoSubscriptionForm from './PromoSubscriptionForm'
import { loadStripe } from '@stripe/stripe-js'
import { envConfig } from '../../../env'
import AuthForm from '@components/auth/AuthForm'
import { TopBanner } from '@components/shared/TopBanner'

export type PropsType = {
  isExpiring: boolean,
  isVisible: boolean,
  isLoggedIn: boolean,
  isSubscribed: boolean,
  isPurchaseSuccess: boolean,
  notificationCompensation?: boolean,
  defaultSignIn: boolean,
  subtype: 'monthly' | 'annual',
  subscriptionOfferCta: string,
  subscriptionOfferTitle: string,
  subscriptionOfferSubtitle: string,
  subscriptionOfferSubtext: string,
  isBlackFriday?: boolean,
  textAuthSubheader: string,
  isFullScreen?: boolean,
  textSubscriptionConfirmationHeader: string,
  textSubscriptionConfirmationSubheader: string,
  pageSlugForPromoEvent?: string,
  existingPromoCode?: string,
  isExistingQuizResult: boolean,
  bannerText?: string,
  isSocialPayButtonsFirst: boolean
}

export type DispatchPropsType = {
  dismissPromoModal: () => void
}
// Lazy load Stripe only when needed with proper error handling
let stripePromise = null
const getStripePromise = (): Promise<any> | null => {
  const stripeKey = envConfig().STRIPE_KEY
  if (!stripeKey) {
    console.warn('Stripe key not found in environment variables')
    return null
  }

  if (!stripePromise) {
    stripePromise = loadStripe(stripeKey).catch((error: any): void => {
      console.error('Failed to load Stripe:', error)
      // Return null to prevent repeated attempts
      stripePromise = null
      throw new Error('Failed to load Stripe.js. Please check your internet connection and try again.')
    })
  }
  return stripePromise
}
export default function ConversionPaywallModal (
  props: PropsType & DispatchPropsType
): React.Node {
  const {
    isExpiring,
    isVisible,
    isLoggedIn,
    isSubscribed,
    isPurchaseSuccess,
    notificationCompensation,
    dismissPromoModal,
    textAuthSubheader,
    isFullScreen,
    isExistingQuizResult,
    bannerText,
    isSocialPayButtonsFirst
  } = props

  // State for Stripe loading and error handling
  const [stripeError, setStripeError] = React.useState(null)

  React.useEffect((): void => {
    // Only load Stripe when modal is visible and user needs to subscribe
    if (isVisible && isLoggedIn && (!isSubscribed || isExpiring)) {
      getStripePromise()
        .then((): void => {
          setStripeError(null)
        })
        .catch((error: any): void => {
          setStripeError(error.message || 'Failed to load payment system')
        })
    }
  }, [isVisible, isLoggedIn, isSubscribed, isExpiring])

  React.useEffect((): void => {
    if (isLoggedIn && isPurchaseSuccess) {
      // trigger api fetch
      if (isExistingQuizResult) {
        dismissPromoModal()
      }
    }
  }, [isPurchaseSuccess])

  const closeModal = (): void => {
    dismissPromoModal()
  }

  if (isLoggedIn && isSubscribed && !isExpiring) {
    return null
  }

  return (
    <Wrapper isBannerVisible={bannerText}>
      {!!bannerText && isVisible && (
        <TopBanner bannerText={bannerText} noNavBar zIndex={1003} />
      )}

      <Modal
        isVisible={isVisible}
        toggleModalFunction={closeModal}
        maxWidth={640}
        notificationCompensation={notificationCompensation || undefined}
        bgGradient={true}
        isFullScreen={!!isFullScreen}
        closeButtonHeight={bannerText ? 74 : 35}
      >
        {isLoggedIn
          ? (
              (!isSubscribed || isExpiring) && (
                stripeError
                  ? (
                  <ErrorContainer>
                    <h3>Payment System Unavailable</h3>
                    <p>{stripeError}</p>
                    <p>Please try again later or contact support if the issue persists.</p>
                  </ErrorContainer>
                    )
                  : (
                  <Elements stripe={getStripePromise()}>
                    <PromoSubscriptionForm
                      subtype={props.subtype}
                      subscriptionOfferCta={props.subscriptionOfferCta}
                      subscriptionOfferTitle={props.subscriptionOfferTitle}
                      subscriptionOfferSubtitle={props.subscriptionOfferSubtitle}
                      subscriptionOfferSubtext={props.subscriptionOfferSubtext}
                      inProcess={isVisible}
                      isSubscribed={props.isSubscribed}
                      price={'69.99'}
                      isBlackFriday={props.isBlackFriday}
                      pageSlugForPromoEvent={props.pageSlugForPromoEvent}
                      existingPromoCode={props.existingPromoCode}
                      isSocialPayButtonsFirst={isSocialPayButtonsFirst}
                    />
                  </Elements>
                    )
              )
            )
          : (
          <AuthContainer isBannerVisible={bannerText}>
            <AuthForm
              theme={'white'}
              subHeader={textAuthSubheader}
              isCenteredAligned
              isOutlineStyle={!isSocialPayButtonsFirst}
              isSocialPayButtonsFirst={isSocialPayButtonsFirst}
            />
          </AuthContainer>
            )}
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  div.#promo-subscription-form {
    ${(props: { isBannerVisible: boolean }): ?string => {
      if (props.isBannerVisible) {
        return 'padding: 112px 0 70px;'
      }
    }}
  }
`

const ErrorContainer = styled.div`
  padding: 40px 20px;
  text-align: center;
  
  h3 {
    color: #333;
    margin-bottom: 16px;
  }
  
  p {
    color: #666;
    margin-bottom: 12px;
  }
`
export const AuthContainer = styled.div`
  margin: 65px 20px 30px;
  display: flex;
  justify-content: center;

   ${(props: { isBannerVisible: boolean }): ?string => {
    if (props.isBannerVisible) {
      return 'margin: 112px 20px 30px;'
    }
  }}

  div.AuthFormSectionContainer {
    justify-content: center;
    max-width: 460px;
  }
`
